import { EMerchantType } from '@if/core/types'
import { PageContainer } from '@if/mui'
import { Box } from '@mui/material'
import { CsvExportButton } from '@src/components/common/CsvExportButton'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useGetMerchantLoanActivityMetrics, useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import { AggregationPeriod, MetricDisplayMode } from '@src/types/MerchantStatistic'
import { Clock } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConversionFunnel } from './components/ConversionFunnel'
import { PerformanceOverTimeChart } from './components/PerformanceOverTimeChart'
import { StatisticsFilters } from './components/StatisticsFilters'
import { StatisticsSummaryCards } from './components/StatisticsSummaryCards'
import {
  getPreviousPeriodRange,
  getQuickDatePresetRange,
  QuickDatePreset,
  sumMetrics,
  useComputeLoanActivityMetricsAggregation,
} from './statistics-hooks'
import { PERFORMANCE_TABS } from './statistics-metrics'

interface FilterState {
  merchantId: string
  fromDate: string
  toDate: string
}

const StatisticsPage = () => {
  const { t } = useTranslation()
  const merchant = useMerchant()

  const [subsidiaryMerchants] = useMerchantByParentId(
    merchant.merchantTypeId === EMerchantType.Banner ? merchant.id : '',
  )

  const defaultRange = useMemo(() => getQuickDatePresetRange(QuickDatePreset.Last30Days), [])
  const [filterState, setFilterState] = useState<FilterState>({
    merchantId: merchant.id,
    fromDate: defaultRange.fromDate,
    toDate: defaultRange.toDate,
  })
  const [aggregation, setAggregation] = useState<AggregationPeriod>(AggregationPeriod.Daily)
  const [displayMode, setDisplayMode] = useState<MetricDisplayMode>(MetricDisplayMode.Count)

  const { merchantId, fromDate, toDate } = filterState
  const [currentMetrics, isLoading, dataUpdatedAt] = useGetMerchantLoanActivityMetrics(merchantId, fromDate, toDate)

  const previousRange = fromDate && toDate ? getPreviousPeriodRange(fromDate, toDate) : null
  const [previousMetrics] = useGetMerchantLoanActivityMetrics(
    merchantId,
    previousRange?.fromDate ?? '',
    previousRange?.toDate ?? '',
  )

  const aggregatedMetrics = useComputeLoanActivityMetricsAggregation(currentMetrics, aggregation)
  const currentTotals = sumMetrics(currentMetrics)
  const previousTotals = sumMetrics(previousMetrics)

  const handleFilterSubmit = (nextMerchantId: string, nextFromDate: string, nextToDate: string) => {
    setFilterState({ merchantId: nextMerchantId, fromDate: nextFromDate, toDate: nextToDate })
  }

  const csvColumns = PERFORMANCE_TABS.flatMap((tab) => [tab.countKey, tab.amountKey])
  const csvHeaders = [
    t('merchantStatistics.merchantLoanActivity.dateColumn'),
    ...csvColumns.map((key) => t(`merchantStatistics.merchantLoanActivity.${key}`)),
  ]
  const csvRows = aggregatedMetrics.map((stat) => [stat.date, ...csvColumns.map((key) => stat[key] ?? 0)])

  const minutesSinceUpdate = dataUpdatedAt ? Math.floor((Date.now() - dataUpdatedAt) / 60_000) : null
  const updatedLabel =
    minutesSinceUpdate === null
      ? ''
      : minutesSinceUpdate < 1
        ? t('merchantStatistics.performance.updatedJustNow')
        : t('merchantStatistics.performance.updatedHoursAgo', {
            hours: Math.max(1, Math.round(minutesSinceUpdate / 60)),
          })

  return (
    <PageContainer
      overline={t('merchantStatistics.performance.overline')}
      title={t('merchantStatistics.performance.title')}
      subtitle={t('merchantStatistics.performance.subtitle', { merchant: merchant.name })}
      chip={updatedLabel ? { label: updatedLabel, icon: Clock } : undefined}
      actions={
        <CsvExportButton
          filename={`statistics-${fromDate}-${toDate}.csv`}
          headers={csvHeaders}
          rows={csvRows}
          disabled={!aggregatedMetrics.length}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <StatisticsFilters
          currentMerchant={merchant}
          subsidiaryMerchants={subsidiaryMerchants}
          defaultFromDate={defaultRange.fromDate}
          defaultToDate={defaultRange.toDate}
          onSubmit={handleFilterSubmit}
          aggregation={aggregation}
          onAggregationChange={setAggregation}
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
        />

        <StatisticsSummaryCards currentTotals={currentTotals} previousTotals={previousTotals} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
          <PerformanceOverTimeChart
            aggregatedMetrics={aggregatedMetrics}
            displayMode={displayMode}
            aggregation={aggregation}
            isLoading={isLoading}
          />
          <ConversionFunnel totals={currentTotals} />
        </Box>
      </Box>
    </PageContainer>
  )
}

export default StatisticsPage
